import { motion } from "framer-motion";
import { Shield, Zap, Terminal, Copy, ExternalLink, AlertTriangle, PlusCircle, Activity, BarChart3 } from "lucide-react";
import { api } from "../lib/apiClient";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import { Brand, LOGO_URL } from "../components/Brand";

const DISCORD_PATH = "M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.892a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419-.0004 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z";

const FEATURES = [
  { icon: Shield, title: "Powerful moderation", desc: "Lockdown, purge, timeout and ban — with reasons logged every time." },
  { icon: Zap, title: "AI that never sleeps", desc: "Live message scanning catches toxicity, scams and raids in seconds." },
  { icon: Terminal, title: "Advanced commands", desc: "Slash commands with per-role permissions you control from here." },
  { icon: Activity, title: "Real-time insights", desc: "Audit stream, warnings, verifications and health reports in one place." },
];

const PreviewCard = () => (
  <div className="relative hidden lg:block">
    <div className="absolute -inset-6 rounded-3xl bg-[#8B5CF6]/10 blur-3xl" />
    <div className="relative bg-[#0D0E15] border border-white/10 p-5 w-[360px] space-y-4" data-testid="login-preview-card">
      <div className="flex items-center gap-2 pb-3 border-b border-white/5">
        <img src={LOGO_URL} alt="" className="w-6 h-6" />
        <span className="mm-chrome text-sm uppercase">ModNode</span>
        <span className="ml-auto flex items-center gap-1.5 text-[10px] font-mono text-[#10B981]"><span className="w-1.5 h-1.5 bg-[#10B981] pulse-dot" />LIVE</span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {[["Members", "24,571", "#7DD3FC"], ["Online", "3,412", "#10B981"], ["Channels", "124", "#F59E0B"], ["Actions", "98", "#EF4444"]].map(([k, v, c]) => (
          <div key={k} className="bg-[#141622] p-3">
            <div className="text-[10px] uppercase tracking-widest text-[#64748B]">{k}</div>
            <div className="font-display font-bold text-xl mt-1" style={{ color: c }}>{v}</div>
          </div>
        ))}
      </div>
      <div className="space-y-2 font-mono text-[11px]">
        {[["BAN", "@raider1337", "#EF4444"], ["TIMEOUT", "@spammer", "#F59E0B"], ["AI FLAG", "#general", "#8B5CF6"]].map(([a, t, c]) => (
          <div key={a} className="flex justify-between text-[#94A3B8]"><span style={{ color: c }}>{a}</span><span>{t}</span></div>
        ))}
      </div>
    </div>
  </div>
);

export default function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [oauthReady, setOauthReady] = useState(false);
  const [discordCfg, setDiscordCfg] = useState(null);
  const [showRedirectHelp, setShowRedirectHelp] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("mm_token");
    if (token) {
      api.get("/auth/me")
        .then(() => {
          const user = JSON.parse(localStorage.getItem("mm_user") || "null");
          toast.success(`Welcome back${user?.username ? `, ${user.global_name || user.username}` : ""}`);
          navigate("/servers", { replace: true });
        })
        .catch(() => {
          localStorage.removeItem("mm_token");
          localStorage.removeItem("mm_user");
          setCheckingSession(false);
        });
    } else {
      setCheckingSession(false);
    }
  }, [navigate]);

  useEffect(() => {
    api.get("/config").then(r => setOauthReady(r.data.oauth_available)).catch(() => {});
    api.get("/auth/discord/url").then(r => setDiscordCfg(r.data)).catch(() => {});
    if (params.get("oauth_error")) {
      setShowRedirectHelp(true);
      toast.error("Discord rejected the redirect. Follow the steps below to fix.");
    }
  }, [params]);

  const discordLogin = async () => {
    try {
      const r = await api.get("/auth/discord/url");
      setDiscordCfg(r.data);
      window.location.href = r.data.url;
    } catch {
      toast.error("Failed to start Discord login");
    }
  };

  const copyRedirect = async () => {
    if (!discordCfg?.redirect_uri) return;
    try {
      await navigator.clipboard.writeText(discordCfg.redirect_uri);
      toast.success("Redirect URI copied");
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };

  const inviteBot = async () => {
    try {
      const r = await api.get("/bot/invite-url");
      window.open(r.data.url, "_blank", "noopener,noreferrer");
      toast.success("Opened Discord in a new tab — pick a server to invite ModNode");
    } catch {
      toast.error("Failed to create invite link");
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="login-page">
      {checkingSession && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#06070A]" data-testid="session-check">
          <div className="flex items-center gap-3 font-mono text-sm text-[#94A3B8]">
            <span className="w-2 h-2 bg-[#8B5CF6] pulse-dot" /> Restoring session…
          </div>
        </div>
      )}
      <div className="mm-orb absolute top-24 left-[30%] w-[420px] h-[420px] rounded-full bg-[#2563EB]/20 blur-[120px] pointer-events-none" />
      <div className="mm-orb absolute top-40 left-[45%] w-[300px] h-[300px] rounded-full bg-[#F97316]/10 blur-[110px] pointer-events-none" style={{ animationDelay: "-6s" }} />

      <nav className="relative z-10 flex items-center justify-between px-6 md:px-12 py-6">
        <Brand />
        <div className="flex items-center gap-6 text-sm text-[#94A3B8]">
          <Link to="/stats" data-testid="login-stats-link" className="hidden sm:flex items-center gap-1.5 hover:text-white transition-colors"><BarChart3 className="w-4 h-4" /> Live stats</Link>
          <button data-testid="login-nav-signin" onClick={discordLogin} className="px-4 py-2 border border-white/10 hover:border-[#8B5CF6] hover:text-white">Sign in</button>
        </div>
      </nav>

      <main className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-10 md:pt-20 pb-24">
        <div className="mm-rule max-w-md mb-10" />
        <div className="grid lg:grid-cols-[1fr_auto] gap-16 items-center">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#10B981]/30 bg-[#10B981]/5 mb-8">
              <span className="w-1.5 h-1.5 bg-[#10B981] pulse-dot" />
              <span className="text-xs text-[#10B981] font-medium">Bot online · ready to deploy</span>
            </div>
            <div className="flex items-center gap-5 mb-6">
              <img src={LOGO_URL} alt="" className="w-16 h-16 md:w-24 md:h-24 drop-shadow-[0_0_30px_rgba(139,92,246,0.6)]" />
              <h1 className="mm-chrome text-5xl sm:text-6xl lg:text-8xl uppercase leading-none" data-testid="login-wordmark">ModNode</h1>
            </div>
            <p className="max-w-2xl text-lg md:text-xl text-[#CBD5E1] leading-relaxed">
              ModNode is built to make <span className="mm-highlight">managing your server easier</span>. Configure your settings, protect your community, and <span className="mm-highlight">command your Discord like an operator.</span>
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <button data-testid="discord-login-btn" onClick={discordLogin}
                className="group flex items-center justify-center gap-3 px-8 py-4 bg-[#8B5CF6] hover:bg-[#7C3AED] text-white font-semibold mm-glow-cyan hover:-translate-y-0.5">
                <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current"><path d={DISCORD_PATH} /></svg>
                Continue with Discord
              </button>
              <button data-testid="invite-bot-btn" onClick={inviteBot}
                className="flex items-center justify-center gap-3 px-8 py-4 border border-white/15 bg-white/[0.03] hover:bg-white/[0.07] hover:border-white/30 text-white font-semibold hover:-translate-y-0.5">
                <PlusCircle className="w-5 h-5 text-[#7DD3FC]" /> Invite ModNode
              </button>
            </div>

            {!oauthReady && (
              <div className="mt-6">
                <button data-testid="redirect-help-toggle" onClick={() => setShowRedirectHelp(v => !v)}
                  className="flex items-center gap-2 text-xs text-[#F59E0B] hover:text-[#F59E0B]/80">
                  <AlertTriangle className="w-3.5 h-3.5" /> Getting "Invalid OAuth2 redirect_uri"? Click to fix →
                </button>
                {showRedirectHelp && discordCfg?.redirect_uri && (
                  <div data-testid="redirect-help-panel" className="mt-3 bg-[#0D0E15] border border-[#F59E0B]/30 p-5 max-w-2xl">
                    <div className="text-xs font-semibold text-[#F59E0B] mb-3">One-time Discord setup</div>
                    <ol className="text-sm text-[#94A3B8] space-y-2 list-decimal list-inside">
                      <li>Open your Discord app's OAuth2 settings:{" "}
                        <a href={`https://discord.com/developers/applications/${discordCfg.client_id}/oauth2`} target="_blank" rel="noopener noreferrer"
                          className="text-[#A78BFA] hover:underline inline-flex items-center gap-1" data-testid="discord-portal-link">
                          Open Portal <ExternalLink className="w-3 h-3" />
                        </a>
                      </li>
                      <li>Under <span className="text-white">Redirects</span>, click <span className="text-white">Add Redirect</span> and paste this exact URL:</li>
                    </ol>
                    <div className="mt-3 flex items-center gap-2 bg-[#141622] border border-white/10 p-2.5">
                      <code data-testid="redirect-uri-value" className="flex-1 font-mono text-xs text-[#10B981] truncate">{discordCfg.redirect_uri}</code>
                      <button data-testid="copy-redirect-btn" onClick={copyRedirect} className="px-2.5 py-1.5 bg-[#10B981] text-black text-[11px] font-bold hover:bg-[#10B981]/80 flex items-center gap-1">
                        <Copy className="w-3 h-3" /> Copy
                      </button>
                    </div>
                    <ol start="3" className="mt-3 text-sm text-[#94A3B8] space-y-2 list-decimal list-inside">
                      <li>Click <span className="text-white">Save Changes</span> in the Discord Portal.</li>
                      <li>Come back here and hit <span className="text-white">Continue with Discord</span> again.</li>
                    </ol>
                  </div>
                )}
              </div>
            )}
          </motion.div>
          <PreviewCard />
        </div>

        <div className="mm-rule max-w-md mt-20 mb-12 ml-auto" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURES.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.08 }}
              className="bg-[#0D0E15] border border-white/10 p-6" data-testid={`login-feature-${i}`}>
              <div className="w-10 h-10 rounded-xl bg-[#8B5CF6]/10 border border-[#8B5CF6]/25 flex items-center justify-center mb-4">
                <f.icon className="w-5 h-5 text-[#A78BFA]" />
              </div>
              <div className="font-display font-semibold text-white">{f.title}</div>
              <div className="mt-1.5 text-sm text-[#94A3B8] leading-relaxed">{f.desc}</div>
            </motion.div>
          ))}
        </div>
      </main>
    </div>
  );
}
